import { INotificationUnion } from "@elit-hair/elitklinik-shared";
import { Button, ButtonOwnProps, ListItemAvatar, ListItemButton, ListItemText, Skeleton, Stack } from "@mui/material";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import InfiniteScrollList from "@/components/InfiniteScrollList";
import { useGetNotifications } from "@/requests/notification.queries";
import { NotificationCategories, NotificationCategoriesList } from "@/types/global";

import NotificationItem from "./NotificationItem";

/**
 * Displays a filterable, infinite-scrolling list of notifications with category filters.
 * Used in both popover and full-page contexts.
 */
const NotificationList = ({
    handleClose = () => {},
    children,
    className,
    maxHeight,
    filterSize = "small",
    dense = true,
}: {
    handleClose?: () => void;
    children?: React.ReactNode;
    className: string;
    maxHeight: number | string;
    filterSize?: ButtonOwnProps["size"];
    dense?: boolean;
}) => {
    const { t } = useTranslation();
    const [filters, setFilters] = useState<{ categories: undefined | NotificationCategories }>({
        categories: undefined,
    });
    const { data, isPending, isFetching, isFetchingNextPage, hasNextPage, fetchNextPage } = useGetNotifications({
        category: filters.categories,
    });

    const renderNotificationItem = (notification: INotificationUnion, index: number) => (
        <NotificationItem
            handleClose={handleClose}
            key={notification.id || index}
            notification={notification}
            category={filters.categories}
        />
    );
    const loading = isPending || isFetching || isFetchingNextPage;
    const notifications: INotificationUnion[] = useMemo(
        () => (data && data.pages.flatMap((page) => page.list)) || [],
        [data]
    );

    return (
        <div className={` overflow-hidden ${className}`}>
            {children}
            <div className="flex  justify-around gap-2 mb-1 border-t border-b flex-wrap p-2 ">
                {NotificationCategoriesList.map((key) => (
                    <Button
                        key={key || "undefined"}
                        size={filterSize}
                        variant={filters.categories === key ? "contained" : "outlined"}
                        onClick={() => setFilters({ ...filters, categories: key })}
                        color={filters.categories === key ? "primary" : "secondary"}
                        sx={{ fontSize: 12 }}
                    >
                        {t("notification_categories." + key)}
                    </Button>
                ))}
            </div>
            <InfiniteScrollList
                dense={dense}
                items={notifications}
                hasNextPage={hasNextPage}
                isLoading={loading}
                onLoadMore={fetchNextPage}
                renderItem={renderNotificationItem}
                emptyComponent={<EmptyNotifications />}
                className="notification-popover-list"
                skeletonComponent={<NotificationSkeleton />}
                maxHeight={maxHeight}
            />
        </div>
    );
};

/**
 * Empty state component displayed when no notifications are found.
 */
function EmptyNotifications() {
    const { t } = useTranslation();
    return (
        <div className="flex-col flex items-center justify-center  w-full h-[360px]">
            <img src="/empty-notifications.png" className="!w-20 "></img>
            <span className="text-gray-500 mt-2">{t("no-notifications")} !</span>
        </div>
    );
}

/**
 * Loading skeleton component that mimics notification list items while loading.
 */
function NotificationSkeleton() {
    const items = Array.from({ length: 5 });
    return (
        <Stack gap={0}>
            {items.map((_, index) => (
                <ListItemButton key={index} className="!duration-200" sx={{ maxHeight: 80 }}>
                    <ListItemAvatar>
                        <Skeleton variant="circular" width={40} height={40} animation="wave" />
                    </ListItemAvatar>
                    <ListItemText
                        primary={
                            <div className="flex flex-col">
                                <div className="flex text-xs font-medium items-center gap-1 justify-center mb-1">
                                    {/* Empty space like in original */}
                                </div>
                                <div className="flex justify-between items-center font-medium gap-1 ">
                                    <Skeleton animation="wave" width={140} height={20} className="text-black" />
                                    <Skeleton
                                        animation="wave"
                                        width={60}
                                        height={12}
                                        className="text-gray-500 text-xs"
                                    />
                                </div>
                            </div>
                        }
                        secondary={
                            <>
                                <Skeleton animation="wave" width="85%" height={20} className="!pr-6 !text-sm block" />
                                <Skeleton animation="wave" width="20%" height={20} className="!pr-6 !text-sm block" />
                            </>
                        }
                    />
                </ListItemButton>
            ))}
        </Stack>
    );
}

export default NotificationList;
